import { useState } from 'react'
import type { ActionPlanWithOwner } from '@/types'
import { ActionPlanCard } from './action-plan-card'
import { ActionPlanForm, type ActionPlanFormData } from './action-plan-form'
import { ReflectionForm } from './reflection-form'
import {
  useActionPlans,
  useCreateActionPlan,
  useUpdateActionPlan,
  useDeleteActionPlan,
  useCompleteActionPlan,
  useArchiveActionPlan,
  useUnarchiveActionPlan,
} from '@/hooks/use-action-plans'

interface ActionPlansSectionProps {
  goalId: string
  isAdmin: boolean
  canManage: boolean
  goalOwnerName?: string
  currentUserId?: string
}

export function ActionPlansSection({ goalId, isAdmin, canManage, goalOwnerName, currentUserId }: ActionPlansSectionProps) {
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingPlan, setEditingPlan] = useState<ActionPlanWithOwner | null>(null)
  const [activeTab, setActiveTab] = useState<'active' | 'archived'>('active')
  const [archivingPlanId, setArchivingPlanId] = useState<string | null>(null)

  const { data: activePlans, isLoading, error } = useActionPlans(goalId, false)
  const { data: archivedPlans } = useActionPlans(goalId, true)
  const plans = activeTab === 'active' ? activePlans : archivedPlans

  const createMutation = useCreateActionPlan()
  const updateMutation = useUpdateActionPlan()
  const deleteMutation = useDeleteActionPlan()
  const completeMutation = useCompleteActionPlan()
  const archiveMutation = useArchiveActionPlan()
  const unarchiveMutation = useUnarchiveActionPlan()

  const isPending =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending ||
    completeMutation.isPending ||
    archiveMutation.isPending ||
    unarchiveMutation.isPending

  const handleCreate = () => {
    setEditingPlan(null)
    setIsFormOpen(true)
  }

  const handleEdit = (plan: ActionPlanWithOwner) => {
    setEditingPlan(plan)
    setIsFormOpen(true)
  }

  const handleSubmit = async (data: ActionPlanFormData) => {
    const startDate = data.start_date ? Math.floor(new Date(data.start_date).getTime() / 1000) : undefined
    const targetDate = data.target_date ? Math.floor(new Date(data.target_date).getTime() / 1000) : undefined

    if (editingPlan) {
      await updateMutation.mutateAsync({
        id: editingPlan.id,
        goalId,
        title: data.title,
        description: data.description || undefined,
        expected_outcome: data.expected_outcome || undefined,
        status: data.status,
        start_date: startDate ?? null,
        target_date: targetDate ?? null,
      })
    } else {
      await createMutation.mutateAsync({
        goal_id: goalId,
        title: data.title,
        description: data.description || undefined,
        expected_outcome: data.expected_outcome || undefined,
        status: data.status,
        start_date: startDate,
        target_date: targetDate,
      })
    }
    setIsFormOpen(false)
    setEditingPlan(null)
  }

  const handleComplete = async (plan: ActionPlanWithOwner) => {
    if (window.confirm('Mark this action plan as completed?')) {
      await completeMutation.mutateAsync({ id: plan.id, goalId })
    }
  }

  const handleDelete = async (plan: ActionPlanWithOwner) => {
    if (window.confirm('Delete this action plan? This cannot be undone.')) {
      await deleteMutation.mutateAsync({ id: plan.id, goalId })
    }
  }

  const handleArchive = (plan: ActionPlanWithOwner) => {
    // Open reflection form in archive mode
    setArchivingPlanId(plan.id)
  }

  const handleArchiveConfirm = async () => {
    if (!archivingPlanId) return
    await archiveMutation.mutateAsync({ id: archivingPlanId, goalId })
    setArchivingPlanId(null)
  }

  const handleUnarchive = async (plan: ActionPlanWithOwner) => {
    await unarchiveMutation.mutateAsync({ id: plan.id, goalId })
  }

  if (isLoading) {
    return (
      <div className="mt-6">
        <h3 className="text-lg font-semibold text-cfa-dark-gray mb-4">Action Plans</h3>
        <div className="animate-pulse space-y-3">
          <div className="h-24 bg-cfa-light-gray rounded-lg" />
          <div className="h-24 bg-cfa-light-gray rounded-lg" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="mt-6">
        <h3 className="text-lg font-semibold text-cfa-dark-gray mb-4">Action Plans</h3>
        <div className="text-danger-500 text-sm">Failed to load action plans</div>
      </div>
    )
  }

  return (
    <div className="mt-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-cfa-dark-gray">
          Action Plans{goalOwnerName ? ` — ${goalOwnerName}'s Goal` : ''}
        </h3>
        {canManage && activeTab === 'active' && (
          <button
            onClick={handleCreate}
            className="px-3 py-1.5 text-sm font-medium text-white bg-brand-blue hover:bg-brand-blue/90 rounded-lg transition-colors"
          >
            + Add Plan
          </button>
        )}
      </div>

      {/* Active/Archived tabs */}
      <div className="flex gap-1 bg-cfa-light-gray rounded-lg p-1 mb-4">
        <button
          onClick={() => setActiveTab('active')}
          className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'active' ? 'bg-white text-cfa-dark-gray shadow-sm' : 'text-cfa-dark-gray/60 hover:text-cfa-dark-gray'
          }`}
        >
          Active {activePlans?.length ? `(${activePlans.length})` : ''}
        </button>
        <button
          onClick={() => setActiveTab('archived')}
          className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'archived' ? 'bg-white text-cfa-dark-gray shadow-sm' : 'text-cfa-dark-gray/60 hover:text-cfa-dark-gray'
          }`}
        >
          Archived {archivedPlans?.length ? `(${archivedPlans.length})` : ''}
        </button>
      </div>

      {/* Plans list */}
      {plans && plans.length > 0 ? (
        <div className="space-y-3">
          {plans.map((plan) => (
            <ActionPlanCard
              key={plan.id}
              plan={plan}
              isAdmin={isAdmin}
              currentUserId={currentUserId}
              onEdit={() => handleEdit(plan)}
              onComplete={() => handleComplete(plan)}
              onDelete={() => handleDelete(plan)}
              onArchive={() => handleArchive(plan)}
              onUnarchive={() => handleUnarchive(plan)}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-8 bg-cfa-light-gray/50 rounded-lg">
          <p className="text-cfa-dark-gray/70 text-sm">
            {activeTab === 'active' ? 'No action plans yet' : 'No archived plans'}
          </p>
          {canManage && activeTab === 'active' && (
            <button
              onClick={handleCreate}
              className="mt-2 text-brand-blue hover:underline text-sm font-medium"
            >
              Create the first action plan
            </button>
          )}
        </div>
      )}

      {/* Form modal */}
      <ActionPlanForm
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false)
          setEditingPlan(null)
        }}
        onSubmit={handleSubmit}
        plan={editingPlan}
        isPending={isPending}
      />

      {/* Archive reflection prompt */}
      {archivingPlanId && (
        <ReflectionForm
          isOpen={!!archivingPlanId}
          onClose={() => setArchivingPlanId(null)}
          planId={archivingPlanId}
          archiveMode
          onArchive={handleArchiveConfirm}
        />
      )}
    </div>
  )
}
